import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { Select } from '@/components/Select/Select';

import styles from './LinkDialog.module.css';

export interface LinkDialogProps {
  title: string;
  description?: ReactNode;
  label: string;
  /** `[value, label]`; an empty value is a valid choice, e.g. "none". */
  options: readonly (readonly [string, string])[];
  initial?: string;
  confirmLabel: string;
  busy?: boolean;
  error?: string | null;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

/** Pick one record to link another to, from a list the caller supplies. */
export function LinkDialog({
  title,
  description,
  label,
  options,
  initial,
  confirmLabel,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: LinkDialogProps) {
  const [value, setValue] = useState(initial ?? options[0]?.[0] ?? '');
  const blocked = options.length === 0 || (initial !== undefined && value === initial);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!blocked) {
      onSubmit(value);
    }
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button disabled={blocked} form="link-dialog" loading={busy} type="submit">
            {confirmLabel}
          </Button>
        </>
      }
      description={description}
      dismissable={!busy}
      onClose={onCancel}
      title={title}
    >
      <form id="link-dialog" className={styles.form} noValidate onSubmit={submit}>
        <Select data-autofocus label={label} onChange={setValue} options={options} value={value} />
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
